'use client';

import { Button, type ButtonProps } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { Image } from '@/app/components/media/image';
import type { ImageProps } from '@/app/components/media/image/types';
import { useRevealAnim } from '@/app/hooks/useRevealAnim';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA, type DataGAProps } from '@/app/utils/createDataGA';

type IsuzuCarePartsSectionItem = Omit<
  ImageProps,
  'width' | 'height' | 'aspectRatio'
> & {
  title: string;
};

export interface IsuzuCarePartsSectionProps {
  className?: string;
  title: string;
  lead?: string;
  description?: string;
  remark?: string;
  buttons?: ButtonProps[];
  items: IsuzuCarePartsSectionItem[];
  tracking?: Omit<DataGAProps, 'click'>;
}

export const IsuzuCarePartsSection = ({
  className,
  items = [],
  title,
  lead,
  description,
  remark,
  buttons = [],
  tracking,
}: IsuzuCarePartsSectionProps) => {
  useRevealAnim({ defaultBaseDelay: 0.04 });

  const renderButtons = (className?: string) => {
    return (
      <ButtonGroup className={cn('mt-6 md:mt-10', className)} data-revealanim>
        {buttons.map((button, index) => (
          <Button
            key={index}
            {...button}
            {...createDataAttributes({ test: `button-${index}` })}
            {...createDataGA({
              click: true,
              text: button.children,
              url: button.href,
              ...tracking,
            })}
          />
        ))}
      </ButtonGroup>
    );
  };

  return (
    <Section
      size="md"
      className={cn('bg-silver-50', className)}
      {...createDataAttributes({ test: 'parts-images-section' })}
      data-revealanim-group
    >
      <Container
        className="where-span:text-inline-wrap mb-6 grid gap-2 lg:mb-10 lg:grid-cols-2 lg:gap-6 xl:mb-16"
        {...createDataAttributes({ test: 'container' })}
      >
        <div className="space-y-2">
          {!!lead && (
            <p
              className="text-body-1 font-noto-bold text-red-500"
              dangerouslySetInnerHTML={{ __html: lead }}
              {...createDataAttributes({ test: 'lead' })}
            />
          )}
          <h2
            data-revealanim
            className="font-display text-h2 whitespace-pre-line"
            dangerouslySetInnerHTML={{ __html: title }}
            {...createDataAttributes({ test: 'heading' })}
          />
        </div>

        {!!(description || buttons.length > 0) && (
          <>
            <div
              className="space-y-4"
              {...createDataAttributes({ test: 'heading-wrapper' })}
            >
              {!!description && (
                <p
                  data-revealanim
                  className="text-body-1 text-gray-700"
                  dangerouslySetInnerHTML={{ __html: description }}
                  {...createDataAttributes({
                    test: 'description',
                  })}
                />
              )}

              {!!remark && (
                <p
                  data-revealanim
                  className="text-caption text-gray-700"
                  dangerouslySetInnerHTML={{ __html: remark }}
                  {...createDataAttributes({ test: 'remark' })}
                />
              )}

              {buttons.length > 0 && renderButtons('hidden lg:flex')}
            </div>
          </>
        )}
      </Container>

      <Container {...createDataAttributes({ test: 'container' })}>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item, index) => (
            <div
              key={index}
              className="flex flex-col items-center gap-4"
              data-revealanim
              {...createDataAttributes({ test: `item-${index}` })}
            >
              <Image
                src={item.src}
                alt={item.alt}
                width={302}
                height={169}
                className="w-full max-w-76"
                objectFit="contain"
                aspectRatio="aspect-3-2"
                sizes="(min-width: 1024px) 320px, 100vw"
              />
              {!!item.title && (
                <h3
                  {...createDataAttributes({ test: `item-title-${index}` })}
                  className="text-body-1 font-noto-bold where-span:text-inline-wrap text-center text-gray-700"
                  dangerouslySetInnerHTML={{ __html: item.title }}
                />
              )}
            </div>
          ))}
        </div>

        {buttons.length > 0 &&
          renderButtons(
            'lg:hidden md:justify-center [&>*]:w-full md:[&>*]:w-auto'
          )}
      </Container>
    </Section>
  );
};
